import { request, authenticatedRequest } from './apiClient';
import { logger } from '../lib/logger';

const log = logger.createNamespace('pushService');

const SW_URL = '/sw.js';

export interface VapidPublicKeyResponse {
  public_key: string;
}

export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionCreate {
  endpoint: string;
  keys: PushSubscriptionKeys;
  device_label?: string | null;
}

export interface PushSubscriptionRecord {
  id: string;
  endpoint: string;
  keys: PushSubscriptionKeys;
  device_label: string | null;
  created_at: string;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

async function doRegisterServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;

  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) {
    log.info('Service worker already active', { scriptURL: existing.active.scriptURL });
    return existing;
  }

  if (existing) {
    log.info('Unregistering inactive service worker');
    await existing.unregister();
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  const registration = await navigator.serviceWorker.register(SW_URL, {
    scope: '/',
    updateViaCache: 'none',
  });

  log.info('Service worker registered, waiting for ready');
  await navigator.serviceWorker.ready;

  if (registration.installing) {
    await new Promise<void>(resolve => {
      registration.installing!.addEventListener('statechange', function handler() {
        if (this.state === 'activated' || this.state === 'installed') {
          this.removeEventListener('statechange', handler);
          resolve();
        }
      });
    });
  }

  if (!navigator.serviceWorker.controller) {
    log.info('Waiting for service worker to take control');
    await new Promise<void>(resolve => {
      navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
    });
  }

  log.info('Service worker active and controlling page');
  return registration;
}

/**
 * Register the service worker early on app load.
 * Safe to call multiple times — subsequent calls return the same promise.
 */
export function initServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!swRegistrationPromise) {
    swRegistrationPromise = doRegisterServiceWorker().catch(err => {
      log.warn('Service worker init failed', { error: err });
      swRegistrationPromise = null;
      return null;
    });
  }
  return swRegistrationPromise;
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  return initServiceWorker();
}

export async function getVapidPublicKey(): Promise<string> {
  const data = await request<VapidPublicKeyResponse>('/push/vapid-public-key');
  return data.public_key;
}

export async function registerPushSubscription(
  subscription: PushSubscriptionCreate,
  deviceLabel?: string | null
): Promise<PushSubscriptionRecord> {
  const body = {
    endpoint: subscription.endpoint,
    keys: subscription.keys,
    device_label: deviceLabel ?? null,
  };
  return authenticatedRequest<PushSubscriptionRecord>('/push/subscriptions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function unregisterPushSubscription(subscriptionId: string): Promise<void> {
  await authenticatedRequest<void>(`/push/subscriptions/${subscriptionId}`, {
    method: 'DELETE',
  });
}

export async function getPushSubscriptions(): Promise<PushSubscriptionRecord[]> {
  return authenticatedRequest<PushSubscriptionRecord[]>('/push/subscriptions');
}

const SUBSCRIPTION_ID_KEY = 'towapp_push_subscription_id';

function saveSubscriptionId(id: string): void {
  try {
    localStorage.setItem(SUBSCRIPTION_ID_KEY, id);
  } catch {
    // ignore
  }
}

function getStoredSubscriptionId(): string | null {
  try {
    return localStorage.getItem(SUBSCRIPTION_ID_KEY);
  } catch {
    return null;
  }
}

function clearStoredSubscriptionId(): void {
  try {
    localStorage.removeItem(SUBSCRIPTION_ID_KEY);
  } catch {
    // ignore
  }
}

export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export type SubscribeToPushResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'sw_failed' | 'vapid_fetch_failed' | 'vapid_empty' | 'push_service_error' | 'invalid_subscription' | 'backend_failed' | 'unknown' };

/**
 * Register service worker, get VAPID key, subscribe via Push API, and register with backend.
 * Call after user has granted notification permission and is authenticated.
 */
export async function subscribeToPush(deviceLabel?: string | null): Promise<SubscribeToPushResult> {
  if (!isPushSupported()) {
    log.warn('Push not supported in this environment');
    return { ok: false, reason: 'unsupported' };
  }

  try {
    // Kick off SW registration (idempotent)
    await initServiceWorker();

    // Diagnostic: verify /sw.js is served as JavaScript, not HTML (SPA rewrite issue)
    try {
      const swResponse = await fetch(SW_URL, { cache: 'no-store' });
      const contentType = swResponse.headers.get('content-type') || '';
      const firstBytes = await swResponse.clone().text().then(t => t.substring(0, 120));
      const looksLikeHtml = firstBytes.trimStart().startsWith('<!') || firstBytes.trimStart().startsWith('<html');
      log.info('SW file check', { status: swResponse.status, contentType, firstBytes, looksLikeHtml });
      if (looksLikeHtml) {
        log.error('/sw.js is serving HTML instead of JavaScript — Amplify/hosting SPA rewrite is intercepting it');
        return { ok: false, reason: 'sw_failed' };
      }
    } catch (fetchErr) {
      log.warn('Could not fetch /sw.js for validation', { error: fetchErr });
    }

    // Use navigator.serviceWorker.ready to guarantee registration has an active worker
    const readyPromise = navigator.serviceWorker.ready;
    const timeoutPromise = new Promise<null>(resolve => setTimeout(() => resolve(null), 10000));
    const registration = await Promise.race([readyPromise, timeoutPromise]);

    if (!registration) {
      log.warn('Service worker did not become active within 10s');
      return { ok: false, reason: 'sw_failed' };
    }

    if (!registration.active) {
      log.warn('Registration from ready has no active worker', {
        installing: registration.installing?.state,
        waiting: registration.waiting?.state,
      });
      return { ok: false, reason: 'sw_failed' };
    }

    let vapidKey: string;
    try {
      vapidKey = await getVapidPublicKey();
    } catch (err) {
      log.warn('Could not fetch VAPID key from backend — push notifications unavailable', { error: err });
      return { ok: false, reason: 'vapid_fetch_failed' };
    }

    if (!vapidKey) {
      log.warn('Backend returned empty VAPID key — push notifications unavailable');
      return { ok: false, reason: 'vapid_empty' };
    }

    log.info('VAPID public key received', { encoded: vapidKey, length: vapidKey.length });

    const applicationServerKey = urlBase64ToUint8Array(vapidKey);

    // Clean up any existing (possibly corrupted) push subscription before subscribing
    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub) {
      log.info('Unsubscribing existing push subscription before re-subscribing', {
        endpoint: existingSub.endpoint,
      });
      try {
        await existingSub.unsubscribe();
      } catch (unsubErr) {
        log.warn('Failed to unsubscribe existing subscription', { error: unsubErr });
      }
    }

    log.info('Pre-subscribe context', {
      notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A',
      isSecureContext: typeof window !== 'undefined' && window.isSecureContext,
      registrationScope: registration.scope,
      activeWorkerState: registration.active.state,
      activeWorkerScriptURL: registration.active.scriptURL,
      hasController: !!navigator.serviceWorker.controller,
      existingSubscription: !!existingSub,
      applicationServerKeyBytes: applicationServerKey.length,
    });

    let subscription: PushSubscription;
    const MAX_RETRIES = 3;
    const BASE_DELAY_MS = 1500;
    let lastErr: Error & { code?: number } | undefined;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
          log.info(`Retrying pushManager.subscribe() (attempt ${attempt + 1}/${MAX_RETRIES + 1}) after ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
        break;
      } catch (subscribeErr) {
        lastErr = subscribeErr as Error & { code?: number };
        const isPushServiceError =
          lastErr?.name === 'AbortError' &&
          (lastErr?.message?.includes('push service') || lastErr?.message?.includes('Registration failed'));

        log.warn(`pushManager.subscribe() attempt ${attempt + 1} failed`, {
          errorName: lastErr?.name,
          errorMessage: lastErr?.message,
          errorCode: 'code' in lastErr ? lastErr.code : undefined,
          isPushServiceError,
        });

        if (!isPushServiceError || attempt === MAX_RETRIES) {
          log.error('pushManager.subscribe() rejected after all retries', subscribeErr, {
            errorName: lastErr?.name,
            errorMessage: lastErr?.message,
            errorCode: 'code' in lastErr ? lastErr.code : undefined,
            stack: lastErr?.stack,
            totalAttempts: attempt + 1,
          });
          return { ok: false, reason: isPushServiceError ? 'push_service_error' : 'unknown' };
        }
      }
    }
    subscription = subscription!;

    const json = subscription.toJSON();
    log.info('Subscription response', {
      endpoint: json.endpoint,
      hasP256dh: !!json.keys?.p256dh,
      hasAuth: !!json.keys?.auth,
      expirationTime: json.expirationTime ?? 'none',
    });

    const endpoint = json.endpoint;
    const keys = json.keys;
    if (!endpoint || !keys?.p256dh || !keys?.auth) {
      log.error('Invalid push subscription from browser', { json });
      return { ok: false, reason: 'invalid_subscription' };
    }

    try {
      const record = await registerPushSubscription(
        { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } },
        deviceLabel
      );
      saveSubscriptionId(record.id);
      log.info('Push subscription registered with backend', { subscriptionId: record.id });
      return { ok: true };
    } catch (err) {
      log.error('Failed to register subscription with backend', err);
      return { ok: false, reason: 'backend_failed' };
    }
  } catch (err) {
    const e = err as Error & { code?: number };
    log.error('Failed to subscribe to push', err, {
      errorName: e?.name,
      errorMessage: e?.message,
      errorCode: 'code' in e ? e.code : undefined,
      stack: e?.stack,
    });
    return { ok: false, reason: 'unknown' };
  }
}

/**
 * Unsubscribe from push: remove from backend and browser.
 */
export async function unsubscribeFromPush(): Promise<void> {
  const subscriptionId = getStoredSubscriptionId();
  if (subscriptionId) {
    try {
      await unregisterPushSubscription(subscriptionId);
    } catch (err) {
      log.warn('Failed to unregister push subscription on server', { error: err });
    }
    clearStoredSubscriptionId();
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_URL);
    if (registration?.pushManager) {
      const sub = await registration.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    }
  } catch (err) {
    log.warn('Failed to unsubscribe from push in browser', { error: err });
  }
}
